/**
 * Event-detail sidebar: renders one ``HailEventDetail`` in TWO groups (F-4C) — the matched PARTNERS
 * (flagged, with consented mailto:/tel: links for MANUAL contact) and the Places LEADS of a
 * CONFIRMED event (map+list only: pin + business-card deep-link; EEA terms bar any other Places
 * content). Until a human confirms the event the leads section is the CONFIRM BUTTON — the S320/S335
 * gate, the only action that spends Places budget. No outreach message is shown for anyone — leads
 * are never auto-contacted. Pure DOM, no framework. Text is escaped before insertion (server data
 * treated as untrusted at the boundary) so a stray ``<`` can never inject markup.
 */

import type { ForecastBlock, HailEventDetail, Lead, PartnerAlert, PredictedImpact } from './api.ts';
import { escapeHtml, safeHttpUrl } from './dom.ts';

/**
 * Outcome of THIS session's confirm call for the shown event (zone expansion / budget note).
 * Not persisted server-side, so it renders only until the next full reload — the moment right
 * after the human pressed the button is exactly when it matters.
 */
export interface ConfirmInfo {
  zone_expanded: boolean;
  note: string | null;
}

function num(value: number | null | undefined, unit: string, digits = 1): string {
  return value == null ? '—' : `${value.toFixed(digits)} ${unit}`;
}

/** Order shops the way the map does: inside-swath first, then ascending distance (closest first). */
function byProximity<T extends { inside: boolean; distance_km: number }>(shops: T[]): T[] {
  return [...shops].sort((a, b) => a.distance_km - b.distance_km);
}

/** One PARTNER row: the partner + its distance/inside label. No message — manual contact only. */
function partnerRow(shop: PartnerAlert): string {
  const where = shop.inside ? 'uvnitř zásahu' : `${shop.distance_km.toFixed(1)} km`;
  // A button (not <li>) so the row is keyboard-focusable and announced as actionable; the focus key
  // travels in a data- attribute and is read back by the delegated click handler.
  return `
    <li class="shop">
      <button type="button" class="shop-btn" data-shop-key="partner:${shop.shop_id}">
        <span class="shop-dot shop-dot-partner" aria-hidden="true"></span>
        <span class="shop-name">${escapeHtml(shop.name)}</span>
        <span class="shop-meta">Partner · ${where}</span>
      </button>
    </li>`;
}

/**
 * One Places LEAD row of a confirmed event: proximity + the business-card deep-link ONLY. EEA terms
 * bar showing any Places content (name/phone/rating) in our UI, so the row is a neutral "Servis" and
 * everything human lives on the Google card the Vizitka link opens. No message and no mailto:/tel: —
 * leads are never auto-contacted (§7 zákona 480/2004 Sb.).
 */
function leadRow(shop: Lead): string {
  const where = shop.inside ? 'uvnitř zásahu' : `${shop.distance_km.toFixed(1)} km`;
  const vizitka = safeHttpUrl(shop.maps_url);
  const link =
    vizitka !== null
      ? `<a href="${escapeHtml(vizitka)}" target="_blank" rel="noopener">Vizitka (Google)</a>`
      : '';
  const dotClass = shop.priority ? 'shop-dot-lead-priority' : 'shop-dot-lead';

  return `
    <li class="shop">
      <button type="button" class="shop-btn" data-shop-key="lead:${escapeHtml(shop.place_id)}">
        <span class="shop-dot ${dotClass}" aria-hidden="true"></span>
        <span class="shop-name">${escapeHtml(shop.category)}</span>
        <span class="shop-meta">Google · ${where}</span>
      </button>
      <div class="lead-links">${link}</div>
    </li>`;
}

/** Hail-damage core first (karosárny/lakovny, autoskla), then the rest — each by proximity. */
function byPriorityThenProximity(shops: Lead[]): Lead[] {
  return [...shops].sort(
    (a, b) => Number(b.priority) - Number(a.priority) || a.distance_km - b.distance_km,
  );
}

/** The matched-partners list (contracted): rows ordered inside-first then by ascending distance. */
function partnerSection(title: string, shops: PartnerAlert[]): string {
  const head = `<h3>${escapeHtml(title)} <span class="count">${shops.length}</span></h3>`;
  if (shops.length === 0) {
    return `<section class="queue queue-partner">${head}
      <p class="hint">Žádný partner v tomto zásahu.</p></section>`;
  }
  return `
    <section class="queue queue-partner">
      ${head}
      <ul class="shops">${byProximity(shops).map(partnerRow).join('')}</ul>
    </section>`;
}

/**
 * One review-only partner row: name + where, but NO message and NO copy action — a review-only
 * underestimated event generated no partner alert by design, so the row is purely informational.
 */
function reviewPartnerRow(shop: PartnerAlert): string {
  const where = shop.inside ? 'uvnitř zásahu' : `${shop.distance_km.toFixed(1)} km`;
  return `
    <li class="shop">
      <button type="button" class="shop-btn" data-shop-key="partner:${shop.shop_id}">
        <span class="shop-dot shop-dot-partner" aria-hidden="true"></span>
        <span class="shop-name">${escapeHtml(shop.name)}</span>
        <span class="shop-meta">Partner v zásahu · ${where} · k ručnímu posouzení</span>
      </button>
    </li>`;
}

/**
 * The review-only partner group of an UNDERESTIMATED event. These partners sit in the swath but the
 * event flagged nobody (it is map/dashboard only — humans decide), so they are shown under a neutral
 * "k ručnímu posouzení" heading, deliberately NOT the prepared-alerts partner queue. Rows are ordered
 * inside-first then by ascending distance. Returns '' when there are none (a normal event).
 */
function reviewPartnerSection(shops: PartnerAlert[]): string {
  if (shops.length === 0) return '';
  const head = `<h3>Partneři v zásahu (k ručnímu posouzení) <span class="count">${shops.length}</span></h3>`;
  return `
    <section class="queue queue-review">
      ${head}
      <p class="hint">Podhodnocený event — automatické upozornění se neposlalo. Posuďte ručně, případně oslovte sami.</p>
      <ul class="shops">${byProximity(shops).map(reviewPartnerRow).join('')}</ul>
    </section>`;
}

/**
 * The LEADS section — the S320/S335 human gate made visible. An UNCONFIRMED event renders the
 * confirm button (the only action that spends Places budget) with an honest explanation; a
 * confirmed one renders the Places lead rows (or an honest empty note), ordered inside-first then
 * by ascending distance.
 */
function leadSection(detail: HailEventDetail, confirmInfo?: ConfirmInfo): string {
  if (detail.leads_confirmed_at === null) {
    return `
      <section class="queue queue-lead">
        <h3>Servisy v okolí (Google)</h3>
        <p class="hint">Leady se hledají až po lidském potvrzení, že kroupy skutečně spadly —
          detekce může být planý poplach.</p>
        <button type="button" class="confirm-leads-btn">Potvrdit kroupy → najít servisy</button>
      </section>`;
  }
  const when = new Date(detail.leads_confirmed_at).toLocaleString('cs-CZ');
  const shops = detail.leads;
  const head = `<h3>Servisy v okolí (Google) <span class="count">${shops.length}</span></h3>`;
  // The rural-fallback outcome must survive in the PANEL: the header status line where it also
  // appears is overwritten by the next periodic refresh, so Martin missed it in the live test.
  // The info lives only in the confirm response (not stored), so it shows until the next reload.
  let fallbackLine = '';
  if (confirmInfo?.note) {
    fallbackLine = `<p class="hint hint-warn">${escapeHtml(confirmInfo.note)}</p>`;
  } else if (confirmInfo?.zone_expanded) {
    fallbackLine = `<p class="hint">V základní zóně bylo servisů málo — vyhledávání se
      automaticky jednorázově rozšířilo do širšího okolí.</p>`;
  }
  const confirmed = `<p class="hint">Potvrzeno ${escapeHtml(when)}.</p>${fallbackLine}`;
  if (shops.length === 0) {
    return `<section class="queue queue-lead">${head}${confirmed}
      <p class="hint">Google v oblasti žádný servis nenašel.</p></section>`;
  }
  return `
    <section class="queue queue-lead">
      ${head}
      ${confirmed}
      <ul class="shops">${byPriorityThenProximity(shops).map(leadRow).join('')}</ul>
    </section>`;
}

const CONFIDENCE_LABEL: Record<PredictedImpact['confidence'], string> = {
  high: 'vysoká',
  medium: 'střední',
  low: 'nízká',
};

/** Build a shop_id -> display name lookup from the event's matched shops, so a predicted impact
 * (which carries only the id) can be shown by name. Falls back to the id when a shop is not in the
 * current match set. */
function shopNameLookup(detail: HailEventDetail): Map<number, string> {
  const names = new Map<number, string>();
  for (const shop of detail.partner_alerts) names.set(shop.shop_id, shop.name);
  for (const shop of detail.review_only_partners) names.set(shop.shop_id, shop.name);
  // Places leads carry no name (EEA) and are never forecast, so they contribute nothing here.
  return names;
}

/** One predicted-impact row: shop name, ETA (relative to the forecast origin), the direct-hit vs
 * near-track wording, carried-forward size, confidence. ``origin`` is the forecast's data timestamp;
 * the ETA is shown as an absolute arrival clocktime so '+25 min' is never read as from-now. */
function impactRow(impact: PredictedImpact, names: Map<number, string>, origin: Date): string {
  const name = names.get(impact.shop_id) ?? `Servis #${impact.shop_id}`;
  const kindClass = impact.kind === 'partner' ? 'partner' : 'lead';
  const kindLabel = impact.kind === 'partner' ? 'Partner' : 'Lead';
  const size =
    impact.predicted_hail_size_cm == null
      ? 'velikost neznámá'
      : `kroupy ~${impact.predicted_hail_size_cm.toFixed(1)} cm`;
  const confidence = CONFIDENCE_LABEL[impact.confidence];
  // A shop merely near the track is not a guaranteed hit; word it as such instead of "zásah".
  const verb = impact.direct ? 'přímý zásah' : 'blízko dráhy';
  const arrival = new Date(origin.getTime() + impact.eta_min * 60_000).toLocaleTimeString('cs-CZ', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `
    <li class="impact">
      <span class="shop-dot shop-dot-${kindClass}" aria-hidden="true"></span>
      <span class="impact-name">${escapeHtml(name)}</span>
      <span class="impact-eta">${verb} ~${impact.eta_min} min (cca ${escapeHtml(arrival)})</span>
      <span class="impact-meta">${kindLabel} · ${escapeHtml(size)} · jistota
        <span class="conf conf-${impact.confidence}">${confidence}</span></span>
    </li>`;
}

/** Compass bearing (deg) -> short Czech 8-point heading, for the movement summary line. */
function bearingLabel(bearingDeg: number): string {
  const dirs = ['S', 'SV', 'V', 'JV', 'J', 'JZ', 'Z', 'SZ'];
  const idx = Math.round(((bearingDeg % 360) + 360) / 45) % 8;
  return dirs[idx] ?? 'S';
}

// A forecast whose origin (last scan) is older than this is likely already over — the ETAs count
// forward from that scan, not from now. ČHMÚ scans land ~every 5 min, so >10 min means a stale feed.
const FORECAST_STALE_MS = 10 * 60_000;

/**
 * "Predikce dopadu" section: the cell's movement vector summary, the per-shop ETAs (sorted soonest
 * first, anchored to the forecast origin), and a prominent caveat that this is MOVEMENT only (the cell
 * may strengthen/weaken). When the origin scan is old the section is flagged as probably-already-past
 * so a stale '+25 min' is not mistaken for an imminent hit. Returns '' when there is no forecast (a
 * brand-new cell with <2 history points, an ended cell, or the synthetic demo).
 */
function forecastSection(forecast: ForecastBlock | null, names: Map<number, string>): string {
  if (forecast === null) return '';
  const origin = new Date(forecast.forecast_origin);
  const speedKmph = forecast.movement.speed_kmpm * 60;
  const move = `Buňka se pohybuje ~${speedKmph.toFixed(0)} km/h směrem ${bearingLabel(
    forecast.movement.bearing_deg,
  )}.`;
  const ageMs = Date.now() - origin.getTime();
  const originLabel = origin.toLocaleString('cs-CZ');
  const freshness =
    ageMs > FORECAST_STALE_MS
      ? `<p class="forecast-stale">Predikce z dat starých ~${Math.round(
          ageMs / 60_000,
        )} min (snímek ${escapeHtml(originLabel)}) — krupobití pravděpodobně již proběhlo.</p>`
      : `<p class="forecast-origin">Časy od posledního snímku ${escapeHtml(originLabel)}.</p>`;
  const sorted = [...forecast.impacted_shops].sort((a, b) => a.eta_min - b.eta_min);
  const impacts =
    sorted.length === 0
      ? `<p class="hint">Žádné servisy v predikovaném pásu (do +45 min).</p>`
      : `<ul class="impacts">${sorted
          .map((impact) => impactRow(impact, names, origin))
          .join('')}</ul>`;
  return `
    <h3>Predikce dopadu <span class="count">${sorted.length}</span></h3>
    <p class="forecast-move">${escapeHtml(move)}</p>
    ${freshness}
    ${impacts}
    <p class="forecast-caveat">${escapeHtml(forecast.caveat)}</p>`;
}

/** Hail over the radar site: the signature is real but MESH cannot size it there. */
function unknownSizeBanner(detail: HailEventDetail): string {
  if (detail.hail_size_cm !== null) return '';
  return `<p class="coverage-flag coverage-underestimated">Velikost krup neznámá — zásah leží přímo nad radarem, kde se velikost neměří. Signatura krupobití je ale jednoznačná; posuď ručně.</p>`;
}

/**
 * Beam-coverage banner (F-CONF). When the radar beam at the cell's range overshoots the 0 C level the
 * MESH size is a LOWER BOUND — the storm is too far from the radar to size correctly. We surface that
 * honestly:
 *   * an UNDERESTIMATED event (size below the gate, kept only because coverage is poor) reads as a
 *     prominent 'PODHODNOCENO' review banner — it generated no auto-preview, a human decides;
 *   * a normal >=2 cm but poorly-covered event reads a softer 'může být větší' note — its partner
 *     preview still fired, the size just may be understated.
 * Returns '' when coverage is good or unknown.
 */
function coverageBanner(detail: HailEventDetail): string {
  if (!detail.size_underestimated) return '';
  const range =
    detail.radar_range_km == null ? '' : ` ~${detail.radar_range_km.toFixed(0)} km od radaru`;
  const conf =
    detail.size_confidence == null
      ? ''
      : ` · jistota ${(detail.size_confidence * 100).toFixed(0)} %`;
  // The review-only PODHODNOCENO banner is driven by the AUTHORITATIVE persisted event-TYPE flag —
  // the same boolean the backend used to suppress the auto-preview — NOT a hardcoded size threshold
  // (which could desync from the configurable alert gate). A normal poorly-covered event gets the
  // softer 'může být větší' note below.
  if (detail.size_underestimated_event) {
    return `<p class="coverage-flag coverage-underestimated">PODHODNOCENO — bouře${escapeHtml(
      range,
    )}, paprsek nad nulovou izotermou; velikost je spodní odhad. Bez automatické zprávy, posuzuje člověk.${escapeHtml(
      conf,
    )}</p>`;
  }
  return `<p class="coverage-flag coverage-could-be-larger">Velikost může být větší — bouře${escapeHtml(
    range,
  )}, paprsek nad nulovou izotermou.${escapeHtml(conf)}</p>`;
}

/**
 * Validation-candidate line (F-CONF complement). The COMPLEMENT of the amber PODHODNOCENO cue: when the
 * detection is CLOSE to the radar and reliably sized (>= the alert gate, good beam coverage) it is worth
 * verifying on the ground, so we surface a subtle, distinct line inviting ground-truth. Kept visually
 * quiet and a different colour family from the amber underestimated banner (the two never co-occur — a
 * validation-grade event is by definition not underestimated). Returns '' when the event is not a
 * validation candidate.
 */
function validationBanner(detail: HailEventDetail): string {
  if (!detail.validation_grade) return '';
  const range =
    detail.radar_range_km == null ? '' : ` (~${detail.radar_range_km.toFixed(0)} km od radaru)`;
  return `<p class="coverage-flag coverage-validation">Validační kandidát — ověřit na zemi${escapeHtml(
    range,
  )}: blízko radaru, spolehlivá velikost.</p>`;
}

export class Sidebar {
  /**
   * @param root           container element the sidebar renders into.
   * @param onShopClick    invoked with a focus key ('partner:<id>' / 'lead:<place_id>') when a
   *                       listed shop is clicked (wired to the map so it can fly to the pin and
   *                       open its popup). Delegated once in the constructor.
   * @param onConfirmLeads invoked when the user clicks the confirm-hail button (the S320/S335 human
   *                       gate) — the caller runs the Places search and re-renders the detail.
   */
  constructor(
    private readonly root: HTMLElement,
    onShopClick: (shopKey: string) => void,
    onConfirmLeads: () => void,
  ) {
    this.root.addEventListener('click', (event) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('.confirm-leads-btn') != null) {
        onConfirmLeads();
        return;
      }
      const button = target?.closest<HTMLElement>('.shop-btn');
      const key = button?.dataset['shopKey'];
      if (key !== undefined) onShopClick(key);
    });
  }

  showPlaceholder(): void {
    this.root.innerHTML = `
      <div class="sidebar-empty">
        <h2>Detail zásahu</h2>
        <p>Klikněte na polygon krupobití na mapě, nebo vyberte zásah v historii.</p>
      </div>`;
  }

  /** Loading state; a custom message covers the long-running confirm search (default fits the rest). */
  showLoading(message = 'Načítám…'): void {
    this.root.innerHTML = `<div class="sidebar-empty"><p>${escapeHtml(message)}</p></div>`;
  }

  showError(message: string): void {
    this.root.innerHTML = `<div class="sidebar-error"><p>Chyba: ${escapeHtml(message)}</p></div>`;
  }

  showEvent(detail: HailEventDetail, confirmInfo?: ConfirmInfo): void {
    const when = new Date(detail.occurred_at).toLocaleString('cs-CZ');

    this.root.innerHTML = `
      <div class="sidebar-event">
        <h2>Zásah #${detail.id}</h2>
        <p class="when">${escapeHtml(when)} · ${escapeHtml(detail.source)}</p>
        <div class="kpis">
          <div class="kpi"><span class="kpi-val">${detail.hail_size_cm == null ? '—' : num(detail.hail_size_cm, 'cm')}</span><span class="kpi-lbl">velikost krup</span></div>
          <div class="kpi"><span class="kpi-val">${num(detail.area_km2, 'km²', 0)}</span><span class="kpi-lbl">plocha</span></div>
        </div>
        ${unknownSizeBanner(detail)}
        ${coverageBanner(detail)}
        ${validationBanner(detail)}
        ${forecastSection(detail.forecast, shopNameLookup(detail))}
        ${reviewPartnerSection(detail.review_only_partners)}
        ${partnerSection('Partneři v zásahu (alert připraven, zatím se neodesílá)', detail.partner_alerts)}
        ${leadSection(detail, confirmInfo)}
      </div>`;
  }
}
